import type { ModuleKey, Permission } from '@sahab/contracts'
import {
  BadgePercent,
  BookOpenText,
  Boxes,
  CalendarClock,
  CalendarDays,
  ChartPie,
  ClipboardCheck,
  Coins,
  Columns2,
  Container,
  FileClock,
  Hourglass,
  type LucideIcon,
  PackageOpen,
  PackageSearch,
  ReceiptText,
  Scale,
  ShoppingBag,
  Store,
  TrendingUp,
  UserRound,
  UsersRound,
  Wallet,
} from 'lucide-react'
import { moduleEnabled } from '../../app/access'
import type { NavLabel, NavTo } from '../shell/nav'

export const REPORT_SECTION_IDS = [
  'financial',
  'sales',
  'purchasing',
  'inventory',
  'pos',
  'work',
  'projects',
] as const
export type ReportSectionId = (typeof REPORT_SECTION_IDS)[number]

export interface ReportEntry {
  id: string
  /** The screen's name (shared with the sidebar, breadcrumbs and command palette). */
  label: NavLabel
  to: NavTo
  /** Hidden without it (UX only: the API enforces the same permission). */
  permission: Permission
  /** Also needed to open it (UX only, as the API): the purchases by supplier report names suppliers (G26). */
  alsoNeeds?: Permission
  /** Hidden when the company does not have this module (the API refuses its routes too). */
  module?: ModuleKey
  icon: LucideIcon
}

export interface ReportSection {
  id: ReportSectionId
  reports: readonly ReportEntry[]
}

/** Every report of the app, by area: the reports page and the command palette read this list. */
export const REPORT_SECTIONS: readonly ReportSection[] = [
  {
    id: 'financial',
    reports: [
      {
        id: 'trial-balance',
        label: 'nav.trialBalance',
        to: '/trial-balance',
        permission: 'reports.financial',
        icon: Scale,
      },
      {
        id: 'profit-and-loss',
        label: 'nav.profitAndLoss',
        to: '/profit-and-loss',
        permission: 'reports.financial',
        icon: TrendingUp,
      },
      {
        id: 'balance-sheet',
        label: 'nav.balanceSheet',
        to: '/balance-sheet',
        permission: 'reports.financial',
        icon: Columns2,
      },
      {
        id: 'cash-flow',
        label: 'nav.cashFlow',
        to: '/cash-flow',
        permission: 'reports.financial',
        icon: Coins,
      },
      {
        id: 'general-ledger',
        label: 'nav.generalLedger',
        to: '/general-ledger',
        permission: 'reports.financial',
        icon: BookOpenText,
      },
      {
        id: 'vat-return',
        label: 'nav.vatReturn',
        to: '/vat-return',
        permission: 'reports.financial',
        icon: ReceiptText,
      },
    ],
  },
  {
    id: 'sales',
    reports: [
      {
        id: 'sales-by-customer',
        label: 'nav.salesByCustomer',
        to: '/sales-by-customer',
        permission: 'sales.read',
        icon: UserRound,
      },
      {
        id: 'sales-by-product',
        label: 'nav.salesByProduct',
        to: '/sales-by-product',
        permission: 'sales.read',
        icon: ShoppingBag,
      },
      {
        id: 'sales-by-period',
        label: 'nav.salesByPeriod',
        to: '/sales-by-period',
        permission: 'sales.read',
        icon: CalendarDays,
      },
      // ADR-049: what each salesperson sold, returned and collected.
      {
        id: 'sales-by-salesperson',
        label: 'nav.salesBySalesperson',
        to: '/sales-by-salesperson',
        permission: 'sales.read',
        icon: BadgePercent,
      },
      {
        id: 'sales-by-user',
        label: 'nav.salesByUser',
        to: '/sales-by-user',
        permission: 'sales.read',
        icon: UsersRound,
      },
      {
        id: 'receivables-aging',
        label: 'nav.receivablesAging',
        to: '/receivables-aging',
        permission: 'sales.read',
        icon: Hourglass,
      },
    ],
  },
  {
    id: 'purchasing',
    reports: [
      {
        id: 'purchases-by-supplier',
        label: 'nav.purchasesBySupplier',
        to: '/purchases-by-supplier',
        permission: 'purchasing.read',
        alsoNeeds: 'suppliers.read',
        icon: Container,
      },
      {
        id: 'purchases-by-product',
        label: 'nav.purchasesByProduct',
        to: '/purchases-by-product',
        permission: 'purchasing.read',
        icon: PackageOpen,
      },
      {
        id: 'payables-aging',
        label: 'nav.payablesAging',
        to: '/payables-aging',
        permission: 'purchasing.read',
        icon: CalendarClock,
      },
    ],
  },
  {
    id: 'inventory',
    reports: [
      {
        id: 'stock-valuation',
        label: 'nav.stockValuation',
        to: '/stock-valuation',
        permission: 'inventory.read',
        icon: Boxes,
      },
      {
        id: 'low-stock',
        label: 'nav.lowStock',
        to: '/low-stock',
        permission: 'inventory.read',
        icon: PackageSearch,
      },
    ],
  },
  {
    id: 'pos',
    reports: [
      {
        id: 'pos-summary',
        label: 'nav.posSummary',
        to: '/pos-summary',
        permission: 'pos.view_shift',
        icon: Store,
      },
    ],
  },
  {
    id: 'work',
    reports: [
      {
        id: 'task-report',
        label: 'nav.taskReport',
        to: '/task-report',
        permission: 'tasks.reports',
        module: 'tasks',
        icon: ClipboardCheck,
      },
    ],
  },
  {
    // Every column is money: the API lists only projects the reader reaches under projects.financials.
    id: 'projects',
    reports: [
      {
        id: 'project-profitability',
        label: 'nav.projectProfitability',
        to: '/project-profitability',
        permission: 'projects.reports',
        module: 'projects',
        icon: ChartPie,
      },
      {
        id: 'project-budget-vs-actual',
        label: 'nav.projectBudgetVsActual',
        to: '/project-budget-vs-actual',
        permission: 'projects.reports',
        module: 'projects',
        icon: Wallet,
      },
      {
        id: 'project-unbilled-work',
        label: 'nav.projectUnbilledWork',
        to: '/project-unbilled-work',
        permission: 'projects.reports',
        module: 'projects',
        icon: FileClock,
      },
      // P6: hours by person; cost and billable value only with their own permissions.
      {
        id: 'project-utilisation',
        label: 'nav.projectUtilisation',
        to: '/project-utilisation',
        permission: 'projects.reports',
        module: 'projects',
        icon: Hourglass,
      },
    ],
  },
]

/** Any one of these opens the reports page. */
export const REPORT_PERMISSIONS: readonly Permission[] = [
  ...new Set(REPORT_SECTIONS.flatMap((section) => section.reports.map((report) => report.permission))),
]

/**
 * The reports the user may open, by area; areas left empty are dropped. A report of a module the
 * company does not have is left out (`modules` absent: every module, as `moduleEnabled` reads it).
 */
export function visibleReports(
  permissions: ReadonlySet<Permission>,
  modules?: readonly ModuleKey[],
): ReportSection[] {
  return REPORT_SECTIONS.flatMap((section) => {
    const reports = section.reports.filter(
      (report) =>
        permissions.has(report.permission) &&
        (!report.alsoNeeds || permissions.has(report.alsoNeeds)) &&
        (!report.module || moduleEnabled(modules, report.module)),
    )
    return reports.length > 0 ? [{ ...section, reports }] : []
  })
}
